import {
  OrderByInput,
  PermissionValues,
  Workspace,
  WorkspaceFilterModel,
  useCurrentAccount,
  useImpersonateWorkspace,
  useSetWorkspaceListOrder,
  useWorkspaceFilter,
  useWorkspaceListOrder,
} from '@gripp/shared-logic';
import {
  BaseColumnCheckbox,
  Colors,
  FilterType,
  InfiniteDataGrid,
  WorkspacesSearchInput,
} from '@gripp/shared-ui';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet, Text, View } from 'react-native';
import { Button } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';
import { FilterBar } from '../../shared/components/filter/filterBar';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { EditWorkspaceMenu } from './editWorkspaceMenu';

export const Workspaces = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const { impersonateWorkspace } = useImpersonateWorkspace();
  const [workspacesSelected, setWorkspacesSelected] = useState<
    string[] | undefined
  >(undefined);
  const showWorkspaceMenu = useMemo(() => {
    return workspacesSelected?.length === 1 ? true : false;
  }, [workspacesSelected]);
  const updateWorkspaceSelection = (rows: any) => {
    setWorkspacesSelected(rows);
  };

  const { containerMaxHeight, listMaxHeight } = useMaxHeight();
  const { data: workspaceOrder } = useWorkspaceListOrder();
  const { mutateAsync: setWorkspaceOrder } = useSetWorkspaceListOrder();
  const columnHelper = createColumnHelper<any>();
  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setWorkspaceOrder(orderBy);
  };
  const { data: workspaceFilter } = useWorkspaceFilter();
  const filter = useMemo(() => {
    const filter: WorkspaceFilterModel =
      workspaceFilter ?? new WorkspaceFilterModel();
    return filter.getGraphQLFilter();
  }, [workspaceFilter]);

  const checkboxColumn = columnHelper.accessor('selected', {
    cell: BaseColumnCheckbox.checkbox,
    header: BaseColumnCheckbox.checkboxHeader,
    meta: { style: styles.checkboxCell },
  });

  const allColumns = useMemo(() => ({
    name: columnHelper.accessor('name', {
      cell: ({ row }) => {
        const original = row.original;
        const onPress = async () => {
          await impersonateWorkspace(original as Workspace);
          navigate('/activities');
        };
        return (
          <Text>
            <Button mode="text" onPress={onPress}>
              {original.name}
            </Button>
          </Text>
        );
      },
      header: () => t('workspace.list.name'),
      meta: { sortable: true },
    }),
    owner: columnHelper.accessor('owner', {
      cell: (info) => <Text>{info.getValue()}</Text>,
      header: () => t('workspace.list.owner'),
      meta: { sortable: true },
    }),
    type: columnHelper.accessor('type', {
      cell: ({ row }) => {
        const original = row.original;
        return (
          <Text>
            {original.type ? t(`workspace.types.${original.type}`) : ''}
          </Text>
        );
      },
      header: () => t('workspace.list.type'),
      meta: { sortable: true },
    }),
    assetsCount: columnHelper.accessor('assetsCount', {
      cell: (info) => <Text>{info.getValue()}</Text>,
      header: () => t('workspace.list.assetsCount'),
      meta: { sortable: true },
    }),
    routinesCount: columnHelper.accessor('routinesCount', {
      cell: (info) => <Text>{info.getValue()}</Text>,
      header: () => t('workspace.list.routinesCount'),
      meta: { sortable: true },
    }),
    peopleCount: columnHelper.accessor('peopleCount', {
      cell: (info) => <Text>{info.getValue()}</Text>,
      header: () => t('workspace.list.peopleCount'),
      meta: { sortable: true },
    }),
    activitiesCount: columnHelper.accessor('activitiesCount', {
      cell: (info) => <Text>{info.getValue()}</Text>,
      header: () => t('workspace.list.activitiesCount'),
      meta: { sortable: true },
    }),
    groupsCount: columnHelper.accessor('groupsCount', {
      cell: (info) => <Text>{info.getValue()}</Text>,
      header: () => t('workspace.list.groupsCount'),
      meta: { sortable: true },
    }),
    createdAt: columnHelper.accessor('createdAt', {
      cell: (info) => {
        if (info.getValue()) {
          const date = moment(info.getValue());
          return <Text>{date.fromNow()}</Text>;
        }
      },
      header: () => t('workspace.list.createdAt'),
      meta: { 
        sortable: true,
        exportable: false, // Hide from export
      },
    }),
    createdDate: columnHelper.accessor('createdDate', {
      cell: () => null, // Hidden column for export only
      header: () => t('workspace.list.createdDate'),
      meta: { 
        sortable: false,
        exportHeader: t('workspace.list.createdDate'),
        exportCell: (data: any) => {
          if (!data.createdAt) return '';
          return new Date(data.createdAt).toISOString().split('T')[0];
        },
        hidden: true, // Hidden from grid display
      },
    }),
    createdTime: columnHelper.accessor('createdTime', {
      cell: () => null, // Hidden column for export only
      header: () => t('workspace.list.createdTime'),
      meta: { 
        sortable: false,
        exportHeader: t('workspace.list.createdTime'),
        exportCell: (data: any) => {
          if (!data.createdAt) return '';
          return new Date(data.createdAt).toISOString().split('T')[1].substring(0, 8);
        },
        hidden: true, // Hidden from grid display
      },
    }),
    createdBy: columnHelper.accessor('createdBy', {
      cell: (info) => <Text>{info.getValue()}</Text>,
      header: () => t('workspace.list.createdBy'),
      meta: { sortable: true },
    }),
    lastScanned: columnHelper.accessor('lastScanned', {
      cell: (info) => {
        if (info.getValue()) {
          const date = moment(info.getValue());
          return <Text>{date.fromNow()}</Text>;
        }
      },
      header: () => t('workspace.list.lastScanned'),
      meta: { 
        sortable: true,
        exportable: false, // Hide from export
      },
    }),
    lastScannedDate: columnHelper.accessor('lastScannedDate', {
      cell: () => null, // Hidden column for export only
      header: () => t('workspace.list.lastScannedDate'),
      meta: { 
        sortable: false,
        exportHeader: t('workspace.list.lastScannedDate'),
        exportCell: (data: any) => {
          if (!data.lastScanned) return '';
          return new Date(data.lastScanned).toISOString().split('T')[0];
        },
        hidden: true, // Hidden from grid display
      },
    }),
    lastScannedTime: columnHelper.accessor('lastScannedTime', {
      cell: () => null, // Hidden column for export only
      header: () => t('workspace.list.lastScannedTime'),
      meta: { 
        sortable: false,
        exportHeader: t('workspace.list.lastScannedTime'),
        exportCell: (data: any) => {
          if (!data.lastScanned) return '';
          return new Date(data.lastScanned).toISOString().split('T')[1].substring(0, 8);
        },
        hidden: true, // Hidden from grid display
      },
    }),
    updatedAt: columnHelper.accessor('updatedAt', {
      cell: (info) => {
        if (info.getValue()) {
          const date = moment(info.getValue());
          return <Text>{date.fromNow()}</Text>;
        }
      },
      header: () => t('workspace.list.updatedAt'),
      meta: { 
        sortable: true,
        exportable: false, // Hide from export
      },
    }),
    updatedDate: columnHelper.accessor('updatedDate', {
      cell: () => null, // Hidden column for export only
      header: () => t('workspace.list.updatedDate'),
      meta: { 
        sortable: false,
        exportHeader: t('workspace.list.updatedDate'),
        exportCell: (data: any) => {
          if (!data.updatedAt) return '';
          return new Date(data.updatedAt).toISOString().split('T')[0];
        },
        hidden: true, // Hidden from grid display
      },
    }),
    updatedTime: columnHelper.accessor('updatedTime', {
      cell: () => null, // Hidden column for export only
      header: () => t('workspace.list.updatedTime'),
      meta: { 
        sortable: false,
        exportHeader: t('workspace.list.updatedTime'),
        exportCell: (data: any) => {
          if (!data.updatedAt) return '';
          return new Date(data.updatedAt).toISOString().split('T')[1].substring(0, 8);
        },
        hidden: true, // Hidden from grid display
      },
    }),
    lastActivity: columnHelper.accessor('lastActivity', {
      cell: (info) => {
        if (info.getValue()) {
          const date = moment(info.getValue());
          return <Text>{date.fromNow()}</Text>;
        }
      },
      header: () => t('workspace.list.lastActivity'),
      meta: { 
        sortable: true,
        exportable: false, // Hide from export
      },
    }),
    lastActivityDate: columnHelper.accessor('lastActivityDate', {
      cell: () => null, // Hidden column for export only
      header: () => t('workspace.list.lastActivityDate'),
      meta: { 
        sortable: false,
        exportHeader: t('workspace.list.lastActivityDate'),
        exportCell: (data: any) => {
          if (!data.lastActivity) return '';
          return new Date(data.lastActivity).toISOString().split('T')[0];
        },
        hidden: true, // Hidden from grid display
      },
    }),
    lastActivityTime: columnHelper.accessor('lastActivityTime', {
      cell: () => null, // Hidden column for export only
      header: () => t('workspace.list.lastActivityTime'),
      meta: { 
        sortable: false,
        exportHeader: t('workspace.list.lastActivityTime'),
        exportCell: (data: any) => {
          if (!data.lastActivity) return '';
          return new Date(data.lastActivity).toISOString().split('T')[1].substring(0, 8);
        },
        hidden: true, // Hidden from grid display
      },
    }),
  }), [t, navigate, impersonateWorkspace, columnHelper]);

  const columns = useMemo(() => {
    const filtered = workspaceFilter?.columns
      ? (workspaceFilter.columns as (keyof typeof allColumns)[]).map((col) => allColumns[col]).filter(Boolean)
      : Object.values(allColumns);
    
    // Always include export-only columns for data export
    const exportColumns = [
      allColumns.createdDate,
      allColumns.createdTime,
      allColumns.lastScannedDate,
      allColumns.lastScannedTime,
      allColumns.updatedDate,
      allColumns.updatedTime,
      allColumns.lastActivityDate,
      allColumns.lastActivityTime,
    ];
    
    // Filter out hidden columns from grid display but keep them for export
    const allColumnsForGrid = [checkboxColumn, allColumns.name, ...filtered, ...exportColumns];
    const gridColumns = allColumnsForGrid.filter(column => !(column.meta as any)?.hidden);
    
    return gridColumns;
  }, [workspaceFilter?.columns, allColumns, checkboxColumn]);

  const RenderEditWorkspaceMenu = () =>
    showWorkspaceMenu && workspacesSelected ? (
      <EditWorkspaceMenu
        workspaceId={workspacesSelected[0]}
        label={t('workspace.menu.editWorkspace')}
      />
    ) : (
      <></>
    );

  useEffect(() => {
    if (!account) return;

    if (!account.hasPermission(PermissionValues.globalAdmin)) {
      navigate(`/accounts/${account.workspace?.id}`);
    }
  }, [account, navigate]);

  return (
    <View>
      <View style={styles.filtersContainer}>
        <View style={styles.searchContainer}>
          <WorkspacesSearchInput />
        </View>
        <FilterBar filterButtonTypes={[FilterType.WORKSPACECOLUMNS]} />
      </View>
      <View
        style={[
          styles.workspacesContainer,
          {
            maxHeight: containerMaxHeight,
          },
        ]}
      >
        <InfiniteDataGrid
          columns={columns}
          query={GET_WORKSPACES}
          dataPrefix="workspace.listWorkspaces"
          modelName="workspace"
          filter={filter}
          orderBy={workspaceOrder}
          pageSize={50}
          hideHeader={false}
          listMaxHeight={listMaxHeight}
          showScrollIndicator={true}
          onOrderByChange={onOrderByChange}
          updateRowsSelected={(rows) => updateWorkspaceSelection(rows)}
          headerContent={RenderEditWorkspaceMenu()}
          exportFileName="workspaces"
        />
      </View>
    </View>
  );
};

const GET_WORKSPACES = `
  query GetWorkspaces($filter: ListWorkspacesFilter, $page: PageRequest, $orderBy: OrderByInput) {
    workspace {
      listWorkspaces(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        offset
        limit
        items {
          id
          name
          type
          owner
          assetsCount
          routinesCount
          peopleCount
          activitiesCount
          groupsCount
          createdAt
          createdBy
          lastScanned
          updatedAt
          lastActivity
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  workspacesContainer: {
    flex: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  export: {
    flexDirection: 'row',
    margin: 5,
  },
  exportAllAccountsContainer: {
    flexDirection: 'column',
    alignSelf: 'flex-end',
  },
  editColumn: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    width: 30,
    maxWidth: 30,
    marginLeft: 50,
  },
  editModal: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Dimensions.get('window').height / 4,
    backgroundColor: Colors.blackInactive,
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 44,
    marginBottom: 20,
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
});
